import React from 'react';
import * as R from 'ramda';
import { bool, func, number, oneOfType, string } from 'prop-types';

// Components
import InfoRounded from '@mui/icons-material/InfoRounded';
import { Avatar, Box, Button, ButtonBase, Card, Typography } from '@mui/material';
import {
  DescriptionRounded,
  HourglassEmptyRounded,
  QueryBuilderRounded,
} from '@mui/icons-material';
import ControlCameraRoundedIcon from '@mui/icons-material/ControlCameraRounded';
import { LoadingRounded } from 'library';

// Utils
import { formatDollarsWithoutDecimals } from 'utils/helpers/format';
import { calculateExpectedSavings } from 'feature/loading-management/utils/calculate';
import * as styles from './SubscriptionBundleCard.styles';

// Title and Icon for features included in the tier
const allFeatureItems = [
  {
    featureTitle: 'Extended Loading',
    icon: <HourglassEmptyRounded />,
  },
  {
    featureTitle: 'Load Sequencing Wizard',
    icon: <LoadingRounded />,
  },
  {
    featureTitle: 'Core API Library Access',
    icon: <ControlCameraRoundedIcon />,
  },
  {
    featureTitle: 'Rack Wait Times',
    icon: <QueryBuilderRounded />,
  },
  {
    featureTitle: 'BOL Services',
    icon: <DescriptionRounded />,
  },
];

const SubscriptionBundleCard = ({
  annualCost,
  annualVolume,
  currentUsage,
  isRecommended,
  isSelected,
  includesBOL,
  onClick,
  openModal,
  overageRate,
  tierRate,
  tierPlanName,
  volume,
  volumeUsage,
}) => {
  // Tier 5 card includes the Free BOLs feature,
  // if the current card being rendered includesBOL, display all features, otherwise filter out that feature
  const filteredFeatureItems = includesBOL
    ? allFeatureItems
    : R.reject(R.propEq('featureTitle', 'BOL Services'), allFeatureItems);

  const getFormattedCosts = () => {
    const val = currentUsage
      ? annualCost
      : calculateExpectedSavings(annualCost, annualVolume, overageRate, tierRate, volumeUsage);
    return val < 0 ? 'Not Recommmended' : formatDollarsWithoutDecimals(val);
  };

  return (
    <Card sx={theme => styles.subscriptionBundleCardWrapper(theme, currentUsage, isRecommended)}>
      {isSelected && <Box sx={styles.selectedBorder} />}
      <>
        {isRecommended && (
          <ButtonBase
            onClick={() => openModal(true)}
            sx={theme => styles.recommendedBanner(theme, isSelected)}
          >
            <Typography sx={{ color: 'primary.dark' }} variant="body2">
              Recommended
            </Typography>
            <InfoRounded sx={styles.infoIcon} />
          </ButtonBase>
        )}
        <Box alignItems="center" display="flex" flexDirection="column" px={2} py={0}>
          <Typography
            sx={{
              mb: 2,
              textAlign: 'center',
              textTransform: 'uppercase',
            }}
            variant="subtitle2"
          >
            {tierPlanName}
          </Typography>
          <Typography sx={{ color: 'primary.dark', mb: 1 }} variant="h6">
            {volume} bbls
          </Typography>
          <Typography sx={{ color: 'primary.dark', mb: 3 }} variant="h5">
            @ ${tierRate}
          </Typography>
          <Typography sx={{ mb: 0.5 }} variant="body2">
            Overage Rate
          </Typography>
          <Typography sx={{ color: 'primary.dark', mb: 2 }} variant="body1">
            {overageRate === 0 ? '$0.000/bbl' : !overageRate ? 'None' : `$${overageRate}/bbl`}
          </Typography>
          {currentUsage && (
            <>
              <Typography sx={{ mb: 0.5 }} variant="body2">
                Annual Cost
              </Typography>
              <Typography sx={{ color: 'primary.dark', mb: 3 }} variant="body1">
                {getFormattedCosts()}
              </Typography>
            </>
          )}
          <Box sx={styles.featureItemsContainer}>
            {!currentUsage &&
              filteredFeatureItems.map(({ featureTitle, icon }) => (
                <Box alignItems="center" display="flex" key={featureTitle} sx={{ mb: 2 }}>
                  <Avatar sx={styles.avatar}>{icon}</Avatar>
                  <Typography sx={{ color: 'primary.main', pl: 1 }} variant="body2">
                    {featureTitle}
                  </Typography>
                </Box>
              ))}
          </Box>
        </Box>
      </>
      {!currentUsage && (
        <Button
          onClick={onClick}
          sx={{
            backgroundColor: isSelected ? 'secondary.light' : 'transparent',
            boxShadow: 'none',
            width: 104,
          }}
          variant="outlined"
        >
          {isSelected ? 'Selected' : 'Select'}
        </Button>
      )}
    </Card>
  );
};

SubscriptionBundleCard.propTypes = {
  annualCost: number,
  annualVolume: number,
  currentUsage: bool,
  includesBOL: bool,
  isRecommended: bool,
  isSelected: bool,
  onClick: func,
  openModal: func,
  overageRate: number,
  tierPlanName: string.isRequired,
  tierRate: oneOfType([number, string]),
  volume: string.isRequired,
  volumeUsage: number,
};

SubscriptionBundleCard.defaultProps = {
  annualCost: null,
  annualVolume: null,
  currentUsage: false,
  includesBOL: false,
  isRecommended: false,
  isSelected: false,
  onClick: R.always(),
  openModal: R.always(),
  overageRate: null,
  tierRate: null,
  volumeUsage: null,
};

export default SubscriptionBundleCard;
